import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/lib/hooks";

const inputCls =
  "h-11 w-full rounded-lg border border-zinc-300 bg-white px-4 text-sm focus:border-[#1A4BFF] focus:outline-none";

export default function AdminSettings() {
  const queryClient = useQueryClient();
  const { data: settings } = useContent("/admin/settings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings && !form) {
      setForm({
        company_name: settings.company_name || "",
        company_fullname: settings.company_fullname || "",
        address: settings.address || "",
        phone: settings.phone || "",
        emails: (settings.emails || []).join(", "),
        online_islemler_url: settings.online_islemler_url || "",
      });
    }
  }, [settings, form]);

  if (!form) return <p className="text-sm text-zinc-500">Yükleniyor...</p>;

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        ...form,
        emails: form.emails.split(",").map((e) => e.trim()).filter(Boolean),
      });
      toast.success("Ayarlar kaydedildi");
      queryClient.invalidateQueries({ queryKey: ["/admin/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/content/settings"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "company_name", label: "Şirket Adı (Kısa)" },
    { key: "company_fullname", label: "Şirket Tam Ünvanı" },
    { key: "address", label: "Adres" },
    { key: "phone", label: "Telefon" },
    { key: "emails", label: "E-postalar (virgülle ayırın)" },
    { key: "online_islemler_url", label: "Online İşlemler URL (yeni sekmede açılır)" },
  ];

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-950">Site Ayarları</h1>
      <p className="mt-1 text-sm text-zinc-500">İletişim bilgileri ve genel site ayarları.</p>

      <div className="mt-6 space-y-5 rounded-2xl border border-zinc-200 bg-white p-7">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="mb-1.5 block text-sm font-bold text-zinc-700">{field.label}</label>
            <input data-testid={`settings-${field.key}`} value={form[field.key]} onChange={update(field.key)} className={inputCls} />
          </div>
        ))}

        <button
          data-testid="settings-save-button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1A4BFF] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0033CC] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}
